/*
 * Botón para activar / desactivar notificaciones push.
 * - Solicita permiso (gesto de usuario).
 * - Se suscribe con el applicationServerKey del backend (/api/push/config).
 * - Envía la suscripción al backend (/api/push/subscribe).
 * - Al desactivar, hace unsubscribe local + POST /api/push/unsubscribe.
 * Se oculta si el navegador no soporta PushManager.
 */
import React, { useEffect, useState } from 'react';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import api from '../../api/client';

const supported = () =>
  typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window;

// Convierte la base64 URL-safe de VAPID a Uint8Array.
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
};

const PushToggle = () => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState(supported() ? Notification.permission : 'denied');

  // Consulta el estado inicial
  useEffect(() => {
    if (!supported()) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setEnabled(Boolean(sub));
      } catch { /* noop */ }
    })();
  }, []);

  const enable = async () => {
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        alert('Permiso denegado. Activa las notificaciones desde los ajustes del navegador.');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const { data: cfg } = await api.get('/push/config');
      if (!cfg?.publicKey) throw new Error('El servidor no tiene claves VAPID configuradas.');
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(cfg.publicKey),
        });
      }
      const payload = sub.toJSON();
      await api.post('/push/subscribe', payload);
      // notificación de bienvenida
      await api.post('/push/test').catch(() => null);
      setEnabled(true);
    } catch (e) {
      alert('No se pudo activar: ' + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  const disable = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const payload = sub.toJSON();
        await api.post('/push/unsubscribe', payload).catch(() => null);
        await sub.unsubscribe();
      }
      setEnabled(false);
    } catch (e) {
      alert('Error al desactivar: ' + (e.message));
    } finally { setLoading(false); }
  };

  if (!supported()) return null;
  const isBlocked = permission === 'denied';

  return (
    <div className="px-3 pb-2">
      <button
        onClick={enabled ? disable : enable}
        disabled={loading || isBlocked}
        data-testid="btn-push-toggle"
        title={isBlocked ? 'Permiso bloqueado — desbloquéalo en los ajustes del navegador' : (enabled ? 'Desactivar notificaciones' : 'Activar notificaciones')}
        className={`w-full flex items-center justify-center gap-1.5 text-[11px] font-bold py-1.5 rounded-lg transition-colors ${
          enabled
            ? 'text-[#A3FF12] hover:bg-white/5'
            : isBlocked
              ? 'text-white/30 cursor-not-allowed'
              : 'text-white/50 hover:text-[#A3FF12] hover:bg-white/5'
        }`}>
        {loading
          ? <Loader2 size={12} className="animate-spin"/>
          : enabled
            ? <Bell size={12}/>
            : <BellOff size={12}/>}
        {enabled ? 'Notificaciones activas' : (isBlocked ? 'Notificaciones bloqueadas' : 'Activar notificaciones')}
      </button>
    </div>
  );
};

export default PushToggle;
